import { useState, type ReactNode } from 'react';
import { Share } from '@capacitor/share';
import { Button, Modal } from '@hrms/ui';
import { copyToClipboard } from '../../../lib/clipboard.js';
import { PRODUCT_NAME } from '../../../lib/brand.js';
import { isNativeApp } from '../../../lib/nativeBrowser.js';
import { courseShareOrigin } from '../../training/ShareCourseActions.js';

/**
 * The company's own address: its subdomain, never the phone app's internal `localhost` origin
 * (same rule as a shared course link, see `courseShareOrigin`).
 */
export function workspaceOrigin(orgSlug: string | null | undefined): string {
  return courseShareOrigin(orgSlug);
}

export function whatsAppInviteText(signInUrl: string): string {
  return (
    `Hi team, we now use ${PRODUCT_NAME} for attendance and leave.\n` +
    `Sign in here with the email I added you with: ${signInUrl}\n` +
    `Then clock in from your phone.`
  );
}

export function whatsAppShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/**
 * "Copy WhatsApp invite": a short message with the workspace sign-in link, for the owner to send
 * to the team. In the phone app it opens the share sheet (WhatsApp is one tap from there); a phone
 * browser with the Web Share API does the same. Elsewhere it copies the message and shows it in a
 * box with "Open WhatsApp", so it still works where the browser refuses the clipboard (as
 * useInviteLink does). The link is the plain sign-in page: it carries no token.
 */
export function useWhatsAppInvite(orgSlug: string | null | undefined) {
  const [shown, setShown] = useState<{ text: string; copied: boolean } | null>(null);

  async function invite() {
    const text = whatsAppInviteText(`${workspaceOrigin(orgSlug)}/login`);
    if (isNativeApp()) {
      try {
        await Share.share({ text, dialogTitle: 'Invite your team' });
        return;
      } catch {
        // Cancelled, or no share sheet: fall through to the box.
      }
    } else if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ text });
        return;
      } catch (error) {
        if ((error as { name?: string } | null)?.name === 'AbortError') return;
      }
    }
    setShown({ text, copied: await copyToClipboard(text) });
  }

  const dialog: ReactNode = shown ? (
    <Modal
      open
      title="WhatsApp invite"
      onClose={() => setShown(null)}
      showCloseButton
      trapFocus
      sheetOnPhone
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-fg-muted">
          {shown.copied
            ? 'Copied. Paste it in your team WhatsApp group, or open WhatsApp to send it.'
            : 'Copy this message and send it to your team on WhatsApp.'}
        </p>
        <textarea
          readOnly
          aria-label="Invite message"
          value={shown.text}
          rows={4}
          onFocus={(event) => event.currentTarget.select()}
          className="w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg"
        />
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={() => setShown(null)}>
            Done
          </Button>
          <a
            href={whatsAppShareUrl(shown.text)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-11 items-center justify-center rounded-md bg-accent px-4 font-medium text-fg-on-accent transition-colors hover:bg-accent-hover sm:h-(--control-height)"
          >
            Open WhatsApp
          </a>
        </div>
      </div>
    </Modal>
  ) : null;

  return { invite, dialog };
}
